import humps from 'lodash-humps-ts';
import { array, boolean, number, object, string } from 'yup';
import { snakes } from '@utils/lodash';
import { authAxios } from '@components/Auth';
import { CarQuote, CarQuoteCompare, CarQuoteList } from '@models/CarQuote';
import { API_V1_BASE } from '@config';
import { CarPlanCategories, CarPlanTypes } from '@models/CarPlan';
import { CommissionShareParams, QuoteUserParams } from '@models/Quote';
import { Numeric, NumericString, WithParams } from '@utils/typescript';
import { OrderCustomTypes } from '@models/Order';

export type CarQuoteOptionParams = WithParams<{
  car_id: number;
  manufactured_year: number;
  category: CarPlanCategories;
  plan_type: CarPlanTypes;
  idv_amount?: NumericString;
  accident_to_driver?: boolean;
  passenger_liability?: boolean;
  flood?: boolean;
  srcc?: boolean;
  promotion_code?: string;
}>;

export type GetAllCarQuoteParams = CarQuoteOptionParams;

export const getAllCarQuoteParamsSchema = object({
  carId: number(),
  manufacturedYear: number(),
  idvAmount: number(),
  category: string(),
  planType: string(),
  accidentToDriver: boolean(),
  passengerLiability: boolean(),
  flood: boolean(),
  srcc: boolean(),
  promotionCode: string(),
});

export type CompareCarQuoteParams = GetAllCarQuoteParams &
  WithParams<{
    car_plan_ids: number[];
  }>;

export const compareCarQuoteParamsSchema = getAllCarQuoteParamsSchema.concat(
  object({
    carPlanIds: array().of(number()),
  }),
);

export type FindCarQuoteParams = GetAllCarQuoteParams &
  WithParams<{
    car_plan_id: number;
  }>;

export const findCarQuoteParamsSchema = getAllCarQuoteParamsSchema.concat(
  object({
    carPlanId: number(),
  }),
);

export type SendEmailCarQuoteParams = FindCarQuoteParams &
  WithParams<{
    name: string;
    email: string;
    note: string;
  }>;

type CarQuoteQuoteParams = WithParams<{
  engine_number: string;
  chassis_number: string;
  registration_place: string;
  registration_number: string;
  activation_date: string;
  company_id: number;
}>;

export type CreateCarQuoteParams = FindCarQuoteParams & QuoteUserParams & CarQuoteQuoteParams & CommissionShareParams;

export type GetCarQuoteParams = {
  orderNumber: string;
};

export type UpdateCarQuoteParams = GetCarQuoteParams & QuoteUserParams & CarQuoteQuoteParams & CommissionShareParams;

export type ResetCarQuoteParams = GetCarQuoteParams &
  WithParams<{
    plan_type: CarPlanTypes;
    idv_amount?: number;
    accident_to_driver?: boolean;
    passenger_liability?: boolean;
    flood?: boolean;
    srcc?: boolean;
  }>;

export type CustomCarQuoteParams = GetCarQuoteParams &
  WithParams<{
    third_party_amount: NumericString;
    third_party_discount_amount: NumericString;
    third_party_deductible_amount: NumericString;
    third_party_deductible_discount_amount: NumericString;
    own_damage_amount: NumericString;
    own_damage_discount_amount: NumericString;
    own_damage_deductible_amount: NumericString;
    own_damage_deductible_discount_amount: NumericString;
    theft_deductible_amount: NumericString;
    theft_deductible_discount_amount: NumericString;
    accident_to_driver_amount: NumericString;
    accident_to_driver_discount_amount: NumericString;
    passenger_liability_amount: NumericString;
    passenger_liability_discount_amount: NumericString;
    admin_fee_amount: NumericString;
    idv_amount: NumericString;
    premium: NumericString;
    flood_amount: NumericString;
    srcc_amount: NumericString;
    no_claim_discount_percentage: Numeric;
    loyalty_discount_percentage: Numeric;
    custom_type: OrderCustomTypes;
    note: string;
    proof: File | undefined;
  }>;

export type LockCarQuoteParams = GetCarQuoteParams &
  WithParams<{
    activation_date: string;
    customer_consent: File | undefined;
  }>;

export type PayByCashCarQuoteParams = GetCarQuoteParams &
  WithParams<{
    proof: File | undefined;
  }>;

export class CarQuoteApi {
  static getAll = async (params: GetAllCarQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/car_quotes`, { params: snakes(params) })
      .then((response) => humps(response.data) as CarQuoteList[]);
  };

  static compare = async (params: CompareCarQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/car_quotes/compare`, { params: snakes(params) })
      .then((response) => humps(response.data) as CarQuoteCompare[]);
  };

  static find = async (params: FindCarQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/car_quotes/find`, { params: snakes(params) })
      .then((response) => humps(response.data) as CarQuote);
  };

  static sendEmail = async (params: SendEmailCarQuoteParams) => {
    return await authAxios.post(`${API_V1_BASE}/car_quotes/send_email`, snakes(params));
  };

  static create = async (params: CreateCarQuoteParams) => {
    return await authAxios
      .post(`${API_V1_BASE}/user/car_quotes`, snakes(params))
      .then((response) => humps(response.data) as CarQuote);
  };

  static get = async ({ orderNumber }: GetCarQuoteParams) => {
    return await authAxios
      .get(`${API_V1_BASE}/user/car_quotes/${encodeURIComponent(orderNumber)}`)
      .then((response) => humps(response.data) as CarQuote);
  };

  static update = async ({ orderNumber, ...rest }: UpdateCarQuoteParams) => {
    return await authAxios
      .put(`${API_V1_BASE}/user/car_quotes/${encodeURIComponent(orderNumber)}`, snakes(rest))
      .then((response) => humps(response.data) as CarQuote);
  };

  static reset = async ({ orderNumber, ...rest }: ResetCarQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/car_quotes/${encodeURIComponent(orderNumber)}/reset`, snakes(rest));
  };

  static custom = async ({ orderNumber, ...rest }: CustomCarQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/car_quotes/${encodeURIComponent(orderNumber)}/custom`, snakes(rest));
  };

  static lock = async ({ orderNumber, ...rest }: LockCarQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/car_quotes/${encodeURIComponent(orderNumber)}/lock`, snakes(rest));
  };

  static payByCash = async ({ orderNumber, ...rest }: PayByCashCarQuoteParams) => {
    return await authAxios
      .putForm(`${API_V1_BASE}/user/car_quotes/${encodeURIComponent(orderNumber)}/pay_by_cash`, snakes(rest));
  };
}
